import React, { useEffect, useRef } from "react";
import styles from "../Modules/ConfirmModal.module.css";
import Button from "./Button/Button.js";

const ICON_MAP = {
    warning:  "bi-exclamation-triangle",
    trash:    "bi-trash3",
    approve:  "bi-check-circle",
    lock:     "bi-lock",
    box:      "bi-box-seam",
    reverse:  "bi-arrow-counterclockwise",
    edit:     "bi-pencil-square",
    whatsapp: "bi-whatsapp",
};

/**
 * Reusable confirmation modal.
 *
 * Props:
 *  - isOpen      {boolean}  – whether the modal is visible
 *  - title       {string}   – bold heading text
 *  - message     {string}   – supporting description
 *  - confirmText {string}   – label for the confirm button  (default: "Confirm")
 *  - cancelText  {string}   – label for the cancel button   (default: "Cancel")
 *  - onConfirm   {function} – called when the user confirms
 *  - onCancel    {function} – called when the user cancels or clicks outside
 *  - icon        {string}   – emoji/icon shown above the title (default: warning triangle)
 *  - confirmDisabled {boolean} – disables Confirm (and its Enter / Y shortcut)
 *  - wide        {boolean}  – wider, scrollable dialog for tables in `children`
 *  - dismissible {boolean}  – false hides Cancel and ignores Esc / N / outside clicks
 *
 * Keyboard: opens with focus on the confirm button, Tab stays inside the dialog,
 * Esc / N cancel, Enter / Y confirm (Enter and Y/N are left alone while typing
 * in a field the caller put in `children`). Marked data-hotkey-scope="modal" so
 * page hotkeys (useHotkeys) pause while it is open.
 */
function ConfirmModal({
    isOpen,
    title = "Are you sure?",
    message = "This action cannot be undone.",
    confirmText = "Confirm",
    cancelText = "Cancel",
    onConfirm,
    onCancel,
    icon = "warning",
    confirmVariant = "dangerSolid",
    confirmDisabled = false,
    wide = false,
    dismissible = true,
    // busy: the action is running — Confirm (and Enter / Y) are disabled so a
    // double click can't fire it twice.
    busy = false,
    children,
}) {
    confirmDisabled = confirmDisabled || busy;
    const modalRef = useRef(null);
    const downOnBackdrop = useRef(false);
    const confirmRef = useRef(null);
    const cancel = dismissible ? onCancel : undefined;
    const handlersRef = useRef({ onConfirm, onCancel: cancel, confirmDisabled });
    handlersRef.current = { onConfirm, onCancel: cancel, confirmDisabled };

    useEffect(() => {
        if (!isOpen) return undefined;
        const previouslyFocused = document.activeElement;
        // Focus the first field the caller supplied (e.g. a reason box), else Confirm.
        const firstField = modalRef.current?.querySelector("input, textarea, select");
        (firstField || confirmRef.current)?.focus();

        const onKeyDown = (e) => {
            const modal = modalRef.current;
            if (!modal) return;
            const t = e.target;
            const typing = t && modal.contains(t) && /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName);
            const onButton = t && modal.contains(t) && t.tagName === "BUTTON";
            const key = e.key;

            if (key === "Escape") {
                e.preventDefault();
                e.stopPropagation();
                handlersRef.current.onCancel?.();
            } else if (key === "Enter" && !typing && !onButton) {
                e.preventDefault();
                e.stopPropagation();
                if (!handlersRef.current.confirmDisabled) handlersRef.current.onConfirm?.();
            } else if (!typing && !e.ctrlKey && !e.metaKey && !e.altKey && (key === "y" || key === "Y")) {
                e.preventDefault();
                if (!handlersRef.current.confirmDisabled) handlersRef.current.onConfirm?.();
            } else if (!typing && !e.ctrlKey && !e.metaKey && !e.altKey && (key === "n" || key === "N")) {
                e.preventDefault();
                handlersRef.current.onCancel?.();
            } else if (key === "Tab") {
                // Focus trap
                const focusables = Array.from(
                    modal.querySelectorAll("button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled])")
                );
                if (!focusables.length) return;
                const first = focusables[0];
                const last = focusables[focusables.length - 1];
                if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
                else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
                else if (!modal.contains(document.activeElement)) { e.preventDefault(); first.focus(); }
            }
        };
        // Capture phase: the dialog owns the keyboard while it is open.
        document.addEventListener("keydown", onKeyDown, true);
        return () => {
            document.removeEventListener("keydown", onKeyDown, true);
            if (previouslyFocused && typeof previouslyFocused.focus === "function" && document.contains(previouslyFocused)) {
                previouslyFocused.focus();
            }
        };
    }, [isOpen]);

    if (!isOpen) return null;

    // Close on a click that STARTED on the backdrop too: selecting text in a
    // reason box and releasing over the backdrop used to cancel the dialog.
    const handleOverlayClick = (e) => {
        const startedOnBackdrop = downOnBackdrop.current;
        downOnBackdrop.current = false;
        if (e.target === e.currentTarget && startedOnBackdrop) cancel?.();
    };

    return (
        <div className={styles.overlay} onMouseDown={(e) => { downOnBackdrop.current = e.target === e.currentTarget; }} onClick={handleOverlayClick}>
            <div ref={modalRef} className={`${styles.modal} ${wide ? styles.modalWide : ""}`} role="dialog" aria-modal="true" aria-labelledby="modal-title" data-hotkey-scope="modal">
                <div className={styles.icon}>
                    <i className={`bi ${ICON_MAP[icon] || ICON_MAP.warning}`} />
                </div>
                <h2 className={styles.title} id="modal-title">{title}</h2>
                {message && <p className={styles.message}>{message}</p>}
                {children}
                <div className={styles.actions}>
                    {dismissible && (
                        <Button
                            variant="outline"
                            size="md"
                            onClick={onCancel}
                            style={{ flex: 1, borderColor: "#d0d0d0", background: "#f5f5f5" }}
                        >
                            {cancelText}
                        </Button>
                    )}
                    <Button
                        ref={confirmRef}
                        variant={confirmVariant}
                        size="md"
                        onClick={onConfirm}
                        disabled={confirmDisabled}
                        style={{ flex: 1 }}
                    >
                        {confirmText}
                    </Button>
                </div>
            </div>
        </div>
    );
}

export default ConfirmModal;
